import { intersection } from 'lodash';
import { set } from 'lodash/fp';
import { subMonths } from 'date-fns';

import { CURRENCY, DATA_TYPE_GMV, ANALYTICS_DIALOGS, AnalyticsOrderType } from '../../constants/analytics';
import { BY_DATE_TYPE_RETURN_CREATED, REPORT_TYPES } from '../../components/tables/reports/consts';
import {
  cancellationsReportColumns,
  returnsReportColumns,
  salesByProductReportColumns,
  salesByChannelReportColumns,
  warehouseReportColumns,
} from '../../components/tables/reports/reportsColumnNames';
import { extractCountriesInfo, getInitialReportState } from '../../utils/analytics';
import { supplierRetailerUtils } from '../../utils/supplierRetailer';
import { removeHiddenColumns } from '../../components/tables/reports/reportsColumnNames/utils';

export const ANALYTICS_SET_FILTER = 'analytics_set_filter';
export const ANALYTICS_SET_ORDER_TYPE = 'analytics_set_order_type';
export const ANALYTICS_SET_CURRENCY = 'analytics_set_currency';
export const ANALYTICS_LOADING = 'analytics_loading';
export const ANALYTICS_SET_ADDITIONAL_DATA = 'analytics_set_additional_data';
export const ANALYTICS_SET_DASHBOARD_ADDITIONAL_DATA = 'analytics_set_dashboard_additional_data';
export const ANALYTICS_DATA_TYPE_CHANGED = 'analytics_data_type_changed';
export const ANALYTICS_SET_RETAILER_MAPPING = 'analytics_set_retailer_mapping';
export const ANALYTICS_SET_REPORTS = 'analytics_set_reports';
export const ANALYTICS_CAN_LOAD_REPORT_DATA = 'analytics_can_load_report_data';
export const ANALYTICS_UPDATE_TABLE = 'analytics_update_table';
export const SET_DOWNLOAD_REPORT_STATUS = '[analytics]SET_DOWNLOAD_REPORT_STATUS';
export const SET_DIALOG = '[analytics]SET_DIALOG';
export const ANALYTICS_SORT = 'analytics_sort';

const endDate = window.localStorage.getItem('analyticsEndDate');
const startDate = window.localStorage.getItem('analyticsStartDate') || subMonths(new Date(), 1);

const initialState = {
  ui: {
    loading: {
      exportModal: false,
      [REPORT_TYPES.SALES]: false,
      [REPORT_TYPES.BEST_SELLERS]: false,
      [REPORT_TYPES.SALES_BY_PRODUCT]: false,
      [REPORT_TYPES.SALES_BY_WAREHOUSE]: false,
      [REPORT_TYPES.RETURNS]: false,
      [REPORT_TYPES.CANCELLATIONS]: false,
      [REPORT_TYPES.SALES_BY_DAY]: false,
      prevData: false,
      countries: false,
      retailers: false,
      canLoadData: false,
      resetPagination: false,
    },
    dialog: {
      [ANALYTICS_DIALOGS.SEND_REPORT_NOTIFICATION]: false,
    },
    downloadReportStatus: null,
  },
  filters: {
    warehouseIds: [],
    retailerIds: [],
    countries: [],
    orderTypes: [],
    dates: {
      start: new Date(startDate),
      end: endDate ? new Date(endDate) : new Date(),
    },
    byDateType: BY_DATE_TYPE_RETURN_CREATED,
  },
  currentCurrency: CURRENCY.USD,
  currentOrderType: AnalyticsOrderType.SALES,
  metricsDataType: {
    'sales-by-channels': DATA_TYPE_GMV.value,
    'best-sellers-by-channel': DATA_TYPE_GMV.value,
    'sales-by-top-channels': DATA_TYPE_GMV.value,
    'sales-report': DATA_TYPE_GMV.value,
  },
  retailerNamesByIds: {},
  countries: {},
  reports: {
    [REPORT_TYPES.SALES]: getInitialReportState(removeHiddenColumns(salesByChannelReportColumns)),
    [REPORT_TYPES.RETURNS]: getInitialReportState(removeHiddenColumns(returnsReportColumns), REPORT_TYPES.RETURNS),
    [REPORT_TYPES.SALES_BY_PRODUCT]: getInitialReportState(
      removeHiddenColumns(salesByProductReportColumns),
      REPORT_TYPES.SALES_BY_PRODUCT,
    ),
    [REPORT_TYPES.SALES_BY_WAREHOUSE]: getInitialReportState(removeHiddenColumns(warehouseReportColumns)),
    [REPORT_TYPES.CANCELLATIONS]: getInitialReportState(
      removeHiddenColumns(cancellationsReportColumns),
      REPORT_TYPES.CANCELLATIONS,
    ),
  },
  additionalData: {
    [REPORT_TYPES.BEST_SELLERS]: { data: null },
    [REPORT_TYPES.SALES_BY_DAY]: { data: null },
    prevData: { data: null },
  },
};

const analytics = (state = initialState, action) => {
  switch (action.type) {
    case ANALYTICS_SET_FILTER: {
      const keys = Object.keys(action.payload);
      const isSameFilter = (key, value) => JSON.stringify(state.filters[key]) === JSON.stringify(value);
      const isSameFilters = keys.every((key) => isSameFilter(key, action.payload[key]));
      if (isSameFilters) return state;

      let finalState = state;
      keys.forEach((key) => {
        const value = action.payload[key];
        finalState = set(['filters', key], value, finalState);
      });
      return finalState;
    }

    case ANALYTICS_SET_CURRENCY:
      return set('currentCurrency', action.payload.currency, state);

    case ANALYTICS_SET_ORDER_TYPE:
      return set('currentOrderType', action.payload.orderType, state);

    case ANALYTICS_LOADING:
      return set(
        'ui.loading',
        {
          ...state.ui.loading,
          ...action.payload,
        },
        state,
      );

    case ANALYTICS_DATA_TYPE_CHANGED:
      return set(['metricsDataType', action.payload.metric], action.payload.dataType, state);

    case ANALYTICS_SET_REPORTS: {
      const { reportType, data, resetPagination } = action.payload;

      let ui = state.ui;

      const reportData = {
        ...state.reports[reportType],
        ...data,
        table: {
          ...state.reports[reportType].table,
        },
      };

      if (resetPagination) {
        reportData.table.page = 0;
        ui = {
          ...ui,
          loading: {
            ...ui.loading,
          },
        };
      }

      return {
        ...state,
        ui,
        reports: {
          ...state.reports,
          [reportType]: reportData,
        },
      };
    }

    case ANALYTICS_CAN_LOAD_REPORT_DATA: {
      const { reportType, canLoadData } = action.payload;

      return {
        ...state,
        reports: {
          ...state.reports,
          [reportType]: {
            ...state.reports[reportType],
            canLoadData,
          },
        },
      };
    }

    case ANALYTICS_SET_ADDITIONAL_DATA: {
      const { kpi, data } = action.payload;
      return {
        ...state,
        additionalData: {
          ...state.additionalData,
          ...data,
        },
        ui: {
          ...state.ui,
          loading: {
            ...state.ui.loading,
            [kpi]: false,
          },
        },
      };
    }

    case ANALYTICS_SET_DASHBOARD_ADDITIONAL_DATA: {
      const { reportType, supplierRetailers = [], retailers = [] } = action.payload;
      const retailerNamesByIds = supplierRetailerUtils.mapRetailersByIdToRetailerNames(supplierRetailers, retailers);
      const payloadCountries = extractCountriesInfo(action.payload.countries.filter((country) => !!country));
      const countries = getSelectedCountries(
        Object.keys(payloadCountries),
        Object.keys(state.countries),
        state.filters.countries,
      );

      const loading = {
        ...state.ui.loading,
        countries: false,
        retailers: false,
      };

      const reports = {
        ...state.reports,
      };

      if (reportType) {
        reports[reportType].canLoadData = true;
      } else {
        loading.canLoadData = true;
      }

      return {
        ...state,
        countries: payloadCountries,
        retailers,
        retailerNamesByIds,
        filters: {
          ...state.filters,
          countries,
        },
        ui: {
          ...state.ui,
          loading,
        },
        reports,
      };
    }

    case ANALYTICS_SET_RETAILER_MAPPING: {
      const { supplierRetailers = [], retailers = [] } = action.payload;
      const retailerNamesByIds = supplierRetailerUtils.mapRetailersByIdToRetailerNames(supplierRetailers, retailers);

      return {
        ...state,
        retailerNamesByIds,
        retailers,
      };
    }

    case ANALYTICS_UPDATE_TABLE: {
      const { reportType, ...payload } = action.payload;
      return set(
        ['reports', reportType, 'table'],
        {
          ...state.reports[reportType].table,
          ...payload,
        },
        state,
      );
    }

    case SET_DOWNLOAD_REPORT_STATUS:
      return set('ui.downloadReportStatus', action.payload.status, state);

    case ANALYTICS_SORT: {
      let { reportType, column, direction } = action.payload;

      return {
        ...state,
        reports: {
          ...state.reports,
          [reportType]: {
            ...set('sort', { column, direction }, state.reports[reportType]),
            table: {
              ...set('page', 0, state.reports[reportType].table),
            },
          },
        },
      };
    }
    case SET_DIALOG: {
      const { dialogId, isOpen } = action.payload;

      return set(`ui.dialog.${dialogId}`, isOpen, state);
    }

    default:
      return state;
  }
};

export default analytics;

function getSelectedCountries(newCountries, currentCountries, selectedCountries) {
  if (selectedCountries.length === 0 || selectedCountries.length === currentCountries.length) {
    return newCountries;
  }

  const countriesInBoth = intersection(newCountries, selectedCountries);
  return countriesInBoth.length === 0 ? newCountries : countriesInBoth;
}
